import { useState, useEffect } from 'react';
import { apiRequest } from '../api/apiService';
import { CHILD_POOLS_COUNT_QUERY, ROOT_POOLS_COUNT_QUERY } from '../api/queries';
import { unifarmFactory } from '../utils/contract';

/**
 * Custom hook to fetch pool counts
 * @param {Object} options - Configuration options
 * @param {string} options.userAddress - Optional: Filter by user address for root pools
 * @param {string} options.childPoolFilterBy - Optional: Filter child pools by creator address (by field)
 * @param {string} options.childPoolFilterPool - Optional: Filter child pools by pool address
 * @param {boolean} options.fetchRootPools - Whether to fetch root pools count (default: true)
 * @param {boolean} options.fetchChildPools - Whether to fetch child pools count (default: true)
 * @param {boolean} options.autoFetch - Whether to fetch automatically on mount (default: true)
 */
const usePoolCounts = ({
    userAddress = null,
    childPoolFilterBy = null,
    childPoolFilterPool = null,
    fetchRootPools = true,
    fetchChildPools = true,
    autoFetch = true
} = {}) => {
    const [rootPoolsCount, setRootPoolsCount] = useState(0);
    const [childPoolsCount, setChildPoolsCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch root pools count
    const fetchRootPoolsCount = async () => {
        try {
            const variables = userAddress
                ? { where: { owner: userAddress } }
                : { where: {} };

            const data = await apiRequest({
                url: unifarmFactory.rootTokens,
                method: "POST",
                data: {
                    query: ROOT_POOLS_COUNT_QUERY,
                    variables,
                },
                auth: false,
            });

            const count = data?.data?.unifarmCreateds?.length || 0;
            setRootPoolsCount(count);
            return count;
        } catch (err) {
            console.error("Error fetching root pools count:", err);
            setError(err);
            return 0;
        }
    };

    // Fetch child pools count
    const fetchChildPoolsCountFn = async () => {
        try {
            // Build the where clause dynamically based on provided filters
            const whereClause = {};

            if (childPoolFilterBy) {
                whereClause.by = childPoolFilterBy;
            }

            if (childPoolFilterPool) {
                whereClause.contract = childPoolFilterPool;
            }

            const variables = { where: whereClause };

            const data = await apiRequest({
                url: unifarmFactory.rootTokens,
                method: "POST",
                data: {
                    query: CHILD_POOLS_COUNT_QUERY,
                    variables,
                },
                auth: false,
            });

            const count = data?.data?.poolCreateds?.length || 0;

            setChildPoolsCount(count);
            return count;
        } catch (err) {
            console.error("Error fetching child pools count:", err);
            setError(err);
            return 0;
        }
    };

    // Fetch all counts
    const fetchCounts = async () => {
        setLoading(true);
        setError(null);

        const promises = [];

        if (fetchRootPools) {
            promises.push(fetchRootPoolsCount());
        }

        if (fetchChildPools) {
            promises.push(fetchChildPoolsCountFn());
        }

        try {
            await Promise.all(promises);
        } catch (err) {
            console.error("Error fetching counts:", err);
        } finally {
            setLoading(false);
        }
    };

    // Refetch function for manual refresh
    const refetch = () => {
        fetchCounts();
    };

    useEffect(() => {
        if (autoFetch) {
            fetchCounts();
        }
    }, [userAddress, childPoolFilterBy, childPoolFilterPool, fetchRootPools, fetchChildPools, autoFetch]);

    return {
        rootPoolsCount,
        childPoolsCount,
        loading,
        error,
        refetch,
    };
};

export default usePoolCounts;